// BudgetFilter.js

import {
    defineComponent,
    ref,
    computed,
    watch,
    onMounted,
    nextTick,
    inject
} from "/Scripts/vue.esm-browser.js";
import { debounce } from "/Scripts/tools.js";
const html = String.raw;

export default defineComponent({
    name: "BudgetFilter",

    props: {
        minValue: {
            type: Number,
            default: 0,
        },
        maxValue: {
            type: Number,
            default: 10000,
        },
        priceRange: {
            type: Array,
            default: [],
        },
    },

    emits: ["update:range"],

    setup(props, { emit }) {
        // Price range state
        const priceRangeMin = ref(props.priceRange[0]);
        const priceRangeMax = ref(props.priceRange[1]);
        const filterCount = inject("filterCount");

        // Drag state
        const isDraggingLeft = ref(false);
        const isDraggingRight = ref(false);

        // DOM refs - will be set in mounted
        const sliderEl = ref(null);
        const barEl = ref(null);
        const leftHandleEl = ref(null);
        const rightHandleEl = ref(null);

        // Update slider bar position
        const updateSliderBar = () => {
            if (!barEl.value || !sliderEl.value) return;

            const totalWidth = sliderEl.value.offsetWidth;
            const leftPercent =
                ((priceRangeMin.value - props.minValue) /
                    (props.maxValue - props.minValue)) *
                100;
            const rightPercent =
                ((props.maxValue - priceRangeMax.value) /
                    (props.maxValue - props.minValue)) *
                100;

            if (leftPercent >= 0 && rightPercent >= 0) {
                barEl.value.style.left = `${leftPercent}%`;
                barEl.value.style.right = `${rightPercent}%`;

                if (leftHandleEl.value) {
                    leftHandleEl.value.style.left = `${leftPercent}%`;
                }

                if (rightHandleEl.value) {
                    rightHandleEl.value.style.left = `${100 - rightPercent}%`;
                }
            }

        };
        const updateRange = () => {
            emit("update:range", {
                min: priceRangeMin.value,
                max: priceRangeMax.value,
            });
        };
        const debouncedUpdateRange = debounce(updateRange, 300);

        // Handle mouse events
        const handleMouseMove = (e) => {
            if (!isDraggingLeft.value && !isDraggingRight.value) return;
            if (!sliderEl.value) return;

            // Get slider dimensions
            const rect = sliderEl.value.getBoundingClientRect();
            const offsetX = e.clientX - rect.left;
            const percent = Math.min(Math.max(0, offsetX / rect.width), 1);
            const newPrice = Math.round(
                props.minValue + percent * (props.maxValue - props.minValue)
            );

            if (isDraggingLeft.value) {
                // Ensure min doesn't exceed max
                priceRangeMin.value = Math.min(
                    newPrice,
                    priceRangeMax.value - 100
                );
            } else if (isDraggingRight.value) {
                // Ensure max doesn't fall below min
                priceRangeMax.value = Math.max(
                    newPrice,
                    priceRangeMin.value + 100
                );
            }
        };

        const handleTouchMove = (e) => {
            e.preventDefault();
            if (e.touches && e.touches[0]) {
                handleMouseMove({
                    clientX: e.touches[0].clientX,
                    clientY: e.touches[0].clientY,
                });
            }
        };

        // Start dragging
        const startDragLeft = () => {
            isDraggingLeft.value = true;
            setupDragEvents();
        };

        const startDragRight = () => {
            isDraggingRight.value = true;
            setupDragEvents();
        };

        // End dragging
        const endDrag = () => {
            isDraggingLeft.value = false;
            isDraggingRight.value = false;
            cleanupDragEvents();
        };

        // Setup global event listeners when dragging starts
        const setupDragEvents = () => {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", endDrag);
            document.addEventListener("touchmove", handleTouchMove, {
                passive: false,
            });
            document.addEventListener("touchend", endDrag);
        };

        // Remove global event listeners when dragging ends
        const cleanupDragEvents = () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", endDrag);
            document.removeEventListener("touchmove", handleTouchMove);
            document.removeEventListener("touchend", endDrag);
        };

        onMounted(() => {
            nextTick(updateSliderBar);
        });
        watch(
            () => props.priceRange,
            (newVal) => {
                priceRangeMin.value = newVal[0];
                priceRangeMax.value = newVal[1];
            },
            { deep: true }
        );

        // Watch for changes and update UI
        watch([priceRangeMin, priceRangeMax], () => {
            nextTick(updateSliderBar);
            debouncedUpdateRange();
        });


        return {
            priceRangeMin,
            priceRangeMax,
            startDragLeft,
            startDragRight,
            sliderEl,
            barEl,
            leftHandleEl,
            rightHandleEl
        };
    },

    template: html`
        <div class="filterbox">
            <div class="filter_title">預算</div>
            <div class="filterSpace_slider">
                <div class="filterSpace_price_wrap">
                    <div class="filterSpace_title">
                        最低
                        <span class="filterSpace_text">
                            NT$ {{ priceRangeMin.toLocaleString() }}
                        </span>
                    </div>
                    <div class="filterSpace_title">
                        最高
                        <span class="filterSpace_text">
                            NT$ {{ priceRangeMax.toLocaleString() }}
                        </span>
                    </div>
                </div>
                <div class="uiSlider" ref="sliderEl">
                    <div class="uiSlider_mainBar">
                        <div class="uiSlider_mainBar_bar" ref="barEl"></div>
                        <div
                            class="uiSlider_handle uiSlider_handle-left"
                            @mousedown="startDragLeft"
                            @touchstart.passive="startDragLeft"
                            ref="leftHandleEl"
                        ></div>
                        <div
                            class="uiSlider_handle uiSlider_handle-right"
                            @mousedown="startDragRight"
                            @touchstart.passive="startDragRight"
                            ref="rightHandleEl"
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    `,
});
